import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { 
  Sun, Utensils, Anchor, Camera,
  Home, MapPin, Car, Waves, Plane, Sunset
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import P1 from "../../assets/portugal/itinerary_images/p1.png";
import P2 from "../../assets/portugal/itinerary_images/p2.png";
import P3 from "../../assets/portugal/itinerary_images/p3.png";
import P4 from "../../assets/portugal/itinerary_images/p4.png";
import P5 from "../../assets/portugal/itinerary_images/p5.png";
import P6 from "../../assets/portugal/itinerary_images/p6.png";
import P7 from "../../assets/portugal/itinerary_images/p7.png";
import P8 from "../../assets/portugal/itinerary_images/p8.png";
import P9 from "../../assets/portugal/itinerary_images/p9.png";
import P10 from "../../assets/portugal/itinerary_images/p10.png";

const days = [
  {
    day: 1,
    date: "May 17",
    dateFull: "SUN MAY 17",
    title: "Flights Depart",
    description: "Travel day. Depart for Lisbon (LIS).",
    color: "bg-orange-500",
    colorLight: "bg-orange-50",
    colorText: "text-orange-600",
    image: P1,
    activities: {
      morning: [
        { time: "TBD", activity: "Group 1 flight departs", location: "Airport (departure)", details: "Depart for Lisbon (LIS).", icon: Plane, mapUrl: "" },
        { time: "TBD", activity: "Group 2 flight departs", location: "Airport (departure)", details: "Depart for Lisbon (LIS).", icon: Plane, mapUrl: "" },
      ],
    },
    notes: [
      "Add exact departure times once tickets are finalized.",
    ],
    overviewActivities: [
      "Depart for Lisbon (LIS).",
    ],
    gettingAround: "Flights to Lisbon (LIS).",
  },
  {
    day: 2,
    date: "May 18",
    dateFull: "MON MAY 18",
    title: "Arrival & Travel to Albufeira",
    description: "Land at Lisbon, metro to Oriente, FlixBus to Albufeira, check in at MarAlto, then lunch, unpack, explore, and dinner.",
    color: "bg-orange-400",
    colorLight: "bg-orange-50",
    colorText: "text-orange-600",
    image: P2,
    activities: {
      morning: [
        { time: "5:50 AM", activity: "Group 1 lands at LIS", location: "Lisbon Airport (LIS)", details: "First group arrives.", icon: Plane, mapUrl: "" },
        { time: "6:30 AM", activity: "Group 2 lands at LIS", location: "Lisbon Airport (LIS)", details: "Second group arrives.", icon: Plane, mapUrl: "" },
        { time: "8:00 AM", activity: "Metro to Oriente Station", location: "Lisbon Airport (LIS) → Lisbon Oriente Station", details: "Subway from the airport to Oriente for the FlixBus.", icon: Car, mapUrl: "https://www.rome2rio.com/map/Lisbon-Airport-LIS/Lisbon-Orient-Station" },
        { time: "8:15 AM", activity: "Get to FlixBus area", location: "Lisbon Oriente Station", details: "Bus departs at 9:00 AM exactly — be on time.", icon: MapPin, mapUrl: "" },
        { time: "11:15 AM", activity: "Arrive at Albufeira bus terminal", location: "R. Paul Harris 63, 8200 Albufeira, Portugal", details: "FlixBus drops off here.", icon: Car, mapUrl: "" },
        { time: "11:30–11:45 AM", activity: "To Airbnb (Uber or bus)", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "~5 min Uber or ~15 min bus to MarAlto.", icon: MapPin, mapUrl: "" },
        { time: "12:00 PM", activity: "Arrive at Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Check in and drop bags.", icon: Home, mapUrl: "" },
      ],
      afternoon: [
        { time: "12:30–1:00 PM", activity: "Lunch", location: "Tenis Pizza – R. Dr. Diogo Leote 9, 8200-121 Albufeira, Portugal", details: "Pizza spot near the Airbnb.", icon: Utensils, mapUrl: "" },
        { time: "1:00–2:00 PM", activity: "Back to Airbnb (unpack, unwind)", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Settle in and relax.", icon: Home, mapUrl: "" },
        { time: "2:00–4:00 PM", activity: "Explore area + grocery shop", location: "Albufeira center", details: "Stock up for the stay.", icon: MapPin, mapUrl: "" },
        { time: "4:00–4:30 PM", activity: "Drop off groceries at Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Quick stop then head out for dinner.", icon: Home, mapUrl: "" },
      ],
      evening: [
        { time: "5:00–7:00 PM", activity: "Dinner", location: "Trattoria Toscana – R. Alm. Gago Coutinho 2, 8200-089 Albufeira, Portugal", details: "Italian dinner in Albufeira.", icon: Utensils, mapUrl: "" },
        { time: "7:30 PM", activity: "Back at Airbnb, shower, bed", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Wind down and rest up for Day 3.", icon: Home, mapUrl: "" },
      ],
    },
    notes: [
      "Metro LIS → Oriente: https://www.rome2rio.com/map/Lisbon-Airport-LIS/Lisbon-Orient-Station",
      "FlixBus departs Oriente at 9:00 AM exactly — don’t miss it.",
      "Airbnb (MarAlto): Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210.",
      "~5 min Uber or ~15 min bus from the terminal to the Airbnb.",
    ],
    overviewActivities: [
      "Land at LIS, metro to Oriente, then FlixBus to Albufeira.",
      "Check in at MarAlto and lunch at Tenis Pizza.",
      "Explore, grocery run, dinner at Trattoria Toscana, then back to the Airbnb.",
    ],
    gettingAround: "Metro LIS → Oriente · FlixBus to Albufeira · Uber or bus in Albufeira.",
  },
  {
    day: 3,
    date: "May 19",
    dateFull: "TUE MAY 19",
    title: "Albufeira Beach Day + Old Town",
    description: "Breakfast, tunnel walk to Praia do Peneco, beach time, then Old Town exploration, dinner, and sunset.",
    color: "bg-teal-500",
    colorLight: "bg-teal-50",
    colorText: "text-teal-600",
    image: P3,
    activities: {
      morning: [
        { time: "7:00 AM", activity: "Wake up", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Start the day slow and easy.", icon: Home, mapUrl: "" },
        { time: "8:00 AM", activity: "Leave Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Head out for breakfast.", icon: Car, mapUrl: "" },
        { time: "8:30 AM", activity: "Arrive at Rutz Cafe for breakfast", location: "Rütz Albufeira, R. 5 de Outubro 54, 8200-151 Albufeira, Portugal", details: "Option: breakfast at Airbnb + stop here for coffee.", icon: Utensils, mapUrl: "" },
        { time: "8:30–9:15 AM", activity: "Breakfast", location: "Rütz Albufeira", details: "Fuel up for the beach morning.", icon: Utensils, mapUrl: "" },
        { time: "9:30 AM", activity: "Walk to the Albufeira Tunnel", location: "Albufeira Tunnel (to Praia do Peneco)", details: "Tunnel walk that takes you to Praia do Peneco Beach.", icon: MapPin, mapUrl: "" },
        { time: "9:30 AM–12:30 PM", activity: "Beach time", location: "Praia do Peneco Beach", details: "Swim, relax, and enjoy the water.", icon: Waves, mapUrl: "" },
      ],
      afternoon: [
        { time: "12:30–1:30 PM", activity: "Lunch by the beach", location: "Cabana Fresca – Largo Cais Herculano, 8200-061 Albufeira, Portugal", details: "Alt option: Ricardo's Pizzaria – 061, R. da Bateria 1, Albufeira, Portugal.", icon: Utensils, mapUrl: "" },
        { time: "1:30 PM", activity: "Leave beach", location: "Praia do Peneco Beach", details: "Head back to the Airbnb.", icon: Car, mapUrl: "" },
        { time: "1:45 PM", activity: "Arrive back at Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Drop off beach stuff and reset.", icon: Home, mapUrl: "" },
        { time: "1:45–3:00 PM", activity: "Shower and get ready", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Get ready for Old Town.", icon: Home, mapUrl: "" },
        { time: "3:00–5:00 PM", activity: "Explore Old Town Albufeira", location: "Old Town Albufeira", details: "Wander, shop, and explore the streets and viewpoints.", icon: MapPin, mapUrl: "" },
      ],
      evening: [
        { time: "5:30–7:30 PM", activity: "Dinner", location: "Três Coroas Restaurant – Três Coroas, R. do Cemitério Velho 8, 8200-000 Albufeira, Portugal", details: "Dinner in Old Town Albufeira.", icon: Utensils, mapUrl: "" },
        { time: "7:30–8:00 PM", activity: "Watch sunset on the beach", location: "Miradouro do Pau da Bandeira, R. Sacadura Cabral 23, 8200-176 Albufeira, Portugal", details: "Possible viewpoint for sunset.", icon: Sunset, mapUrl: "" },
        { time: "8:30 PM", activity: "Arrive back at Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Wind down.", icon: Home, mapUrl: "" },
        { time: "9:30–10:00 PM", activity: "Bed time", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Rest up for Day 4.", icon: Home, mapUrl: "" },
      ],
    },
    notes: [
      "You can have breakfast at the Airbnb and just stop at Rütz for coffee.",
      "Bring towel, sunscreen, and a change of clothes for after the beach.",
    ],
    overviewActivities: [
      "Breakfast at Rütz (or coffee stop) then walk through the tunnel to Praia do Peneco.",
      "Beach time and lunch by the water, then reset at the Airbnb.",
      "Old Town exploration, dinner at Três Coroas, and sunset viewpoint.",
    ],
    gettingAround: "Walking around Albufeira + short Uber if needed.",
  },
  {
    day: 4,
    date: "May 20",
    dateFull: "WED MAY 20",
    title: "Secret Algarve Kayak Day",
    description: "Kayak tour at Benagil, lunch and beach time nearby, sunset, then back to Albufeira.",
    color: "bg-green-500",
    colorLight: "bg-green-50",
    colorText: "text-green-600",
    image: P4,
    activities: {
      morning: [
        { time: "7:00 AM", activity: "Wake up", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Early start for the kayak day.", icon: Home, mapUrl: "" },
        { time: "8:30 AM", activity: "Leave Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Head out for breakfast.", icon: Car, mapUrl: "" },
        { time: "8:30–9:30 AM", activity: "Breakfast", location: "Albufeira", details: "Fuel up before the drive to Benagil.", icon: Utensils, mapUrl: "" },
        { time: "9:30 AM", activity: "Uber to Secret Algarve (kayak tour)", location: "Estr. de Benagil, Benagil, 8400-400 Carvoeiro, Portugal", details: "Secret Algarve kayak tour meeting point.", icon: Car, mapUrl: "" },
        { time: "10:00 AM", activity: "Arrive at Secret Algarve", location: "Estr. de Benagil, Benagil, 8400-400 Carvoeiro, Portugal", details: "Check in for the tour.", icon: MapPin, mapUrl: "" },
        { time: "10:30 AM–12:30 PM", activity: "Kayak", location: "Secret Algarve · Benagil", details: "$54 pp · Aarin, Jade, Avery & Jordan ($216 total). Guided kayak along the coast and caves.", icon: Anchor, mapUrl: "" },
      ],
      afternoon: [
        { time: "12:45–1:45 PM", activity: "Lunch", location: "Benagil / Carvoeiro area", details: "Grab lunch near the tour area.", icon: Utensils, mapUrl: "" },
        { time: "1:45–3:00 PM", activity: "Explore area", location: "Benagil / Carvoeiro", details: "Wander the coastline and nearby spots.", icon: MapPin, mapUrl: "" },
        { time: "3:30–5:30 PM", activity: "Relax on the beach", location: "Benagil area beach", details: "Unwind by the water before dinner.", icon: Waves, mapUrl: "" },
      ],
      evening: [
        { time: "6:00–7:30 PM", activity: "Dinner", location: "TBD", details: "Pick a spot nearby — location to confirm.", icon: Utensils, mapUrl: "" },
        { time: "7:30–8:00 PM", activity: "Watch sunset by the beach", location: "Benagil / Carvoeiro coast", details: "Sunset views before heading back.", icon: Sunset, mapUrl: "" },
        { time: "8:00–8:30 PM", activity: "Uber/Bolt back to Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Ride back to Albufeira.", icon: Car, mapUrl: "" },
        { time: "9:00 PM", activity: "Wind down / bed", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Rest up after a full day.", icon: Home, mapUrl: "" },
      ],
    },
    notes: [
      "Kayak tour: $54 pp · Aarin, Jade, Avery & Jordan ($216 total).",
      "Secret Algarve meeting point: Estr. de Benagil, Benagil, 8400-400 Carvoeiro, Portugal.",
      "Bring swimwear, towel, sunscreen, and a change of clothes.",
      "Dinner location still TBD.",
    ],
    overviewActivities: [
      "Breakfast in Albufeira, then Uber to Secret Algarve for a Benagil kayak tour.",
      "Lunch, explore the area, and beach time in the afternoon.",
      "Dinner (TBD), sunset by the coast, then Uber/Bolt back to the Airbnb.",
    ],
    gettingAround: "Uber/Bolt Albufeira ↔ Benagil (Secret Algarve).",
  },
  {
    day: 5,
    date: "May 21",
    dateFull: "THU MAY 21",
    title: "Carvoeiro & Seven Hanging Valleys",
    description: "Day trip to Carvoeiro for breakfast, town, beach, Algar Seco, then the Seven Hanging Valleys trail.",
    color: "bg-blue-400",
    colorLight: "bg-blue-50",
    colorText: "text-blue-600",
    image: P5,
    activities: {
      morning: [
        { time: "8:00 AM", activity: "Wake up", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Start the day.", icon: Home, mapUrl: "" },
        { time: "8:00–9:00 AM", activity: "Get ready", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Pack for Carvoeiro, beach, and the trail.", icon: Home, mapUrl: "" },
        { time: "9:30 AM", activity: "Uber to Carvoeiro", location: "Carvoeiro", details: "Ride over from Albufeira.", icon: Car, mapUrl: "" },
        { time: "10:00 AM", activity: "Arrive in Carvoeiro", location: "Carvoeiro", details: "Drop into town.", icon: MapPin, mapUrl: "" },
        { time: "10:00–11:00 AM", activity: "Breakfast", location: "Restaurante Encci, Rampa Mato Serrão nº 3, 8400-521 Carvoeiro, Portugal", details: "Sit-down breakfast in Carvoeiro.", icon: Utensils, mapUrl: "" },
        { time: "11:00–11:30 AM", activity: "Grab pastry", location: "Chef Cafe, R. do Barranco, 8400-508 Carvoeiro, Portugal", details: "Pastry stop before exploring.", icon: Utensils, mapUrl: "" },
        { time: "11:30 AM–1:30 PM", activity: "Explore area / town", location: "Carvoeiro", details: "Wander the streets and waterfront.", icon: MapPin, mapUrl: "" },
      ],
      afternoon: [
        { time: "1:30–3:30 PM", activity: "Beach", location: "Carvoeiro beach", details: "Swim and relax by the water.", icon: Waves, mapUrl: "" },
        { time: "3:30–5:30 PM", activity: "Algar Seco", location: "Algar Seco · Boneca Bar Restaurant", details: "Coastal boardwalk and rock formations; stop at Boneca Bar.", icon: Camera, mapUrl: "" },
        { time: "5:30–7:30 PM", activity: "Walk the Seven Hanging Valleys trail", location: "Seven Hanging Valleys Trail", details: "Scenic coastal hike along the cliffs.", icon: MapPin, mapUrl: "" },
      ],
      evening: [
        { time: "7:30–8:00 PM", activity: "Uber from trail end", location: "Trail end → Albufeira", details: "Ride back from wherever the trail finishes.", icon: Car, mapUrl: "" },
      ],
    },
    notes: [
      "Wear sturdy shoes and bring water for the Seven Hanging Valleys trail.",
      "Boneca Bar is a good stop during Algar Seco — grab a drink or bite if you want.",
    ],
    overviewActivities: [
      "Uber to Carvoeiro for breakfast at Encci and pastries at Chef Cafe.",
      "Explore town, beach time, then Algar Seco with a stop at Boneca Bar.",
      "Hike the Seven Hanging Valleys trail and Uber back from the trail end.",
    ],
    gettingAround: "Uber/Bolt Albufeira ↔ Carvoeiro and from trail end.",
  },
  {
    day: 6,
    date: "May 22",
    dateFull: "FRI MAY 22",
    title: "Albufeira → Lisbon + Sunset Cruise",
    description: "Pack up, FlixBus back to Lisbon, explore Pink Street and Time Out Market, then a sunset boat tour on the Tagus.",
    color: "bg-blue-500",
    colorLight: "bg-blue-50",
    colorText: "text-blue-600",
    image: P6,
    activities: {
      morning: [
        { time: "7:30 AM", activity: "Wake up", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Last morning in Albufeira.", icon: Home, mapUrl: "" },
        { time: "7:30–8:00 AM", activity: "Get ready for the day", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Shower and prep for travel.", icon: Home, mapUrl: "" },
        { time: "8:00–9:00 AM", activity: "Pack up", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Check out and load bags.", icon: Home, mapUrl: "" },
        { time: "9:30–10:00 AM", activity: "Leave Airbnb", location: "Rua Joaquim Pedro Samora 26, Albufeira, Faro 8200-210", details: "Head to the FlixBus stop.", icon: Car, mapUrl: "" },
        { time: "10:30 AM", activity: "Arrive at FlixBus stop", location: "Av. da Liberdade 136, 8200-002 Albufeira, Portugal", details: "Board on time — bus departs at 11:00 AM.", icon: MapPin, mapUrl: "" },
        { time: "11:00 AM–2:00 PM", activity: "Travel back to Lisbon", location: "FlixBus · Albufeira → Lisbon", details: "~3 hr ride to Lisbon.", icon: Car, mapUrl: "" },
      ],
      afternoon: [
        { time: "2:30 PM", activity: "Arrive at Lisbon Airbnb", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Check in and drop bags.", icon: Home, mapUrl: "" },
        { time: "3:00 PM", activity: "Arrive at Pink Street", location: "Pink Street (Rua Nova do Carvalho), Lisbon", details: "Head into Cais do Sodré / Pink Street.", icon: MapPin, mapUrl: "" },
        { time: "3:00–3:30 PM", activity: "Explore area", location: "Pink Street, Lisbon", details: "Walk around before Time Out Market.", icon: Camera, mapUrl: "" },
        { time: "4:00–5:00 PM", activity: "Time Out Market", location: "Time Out Market Lisboa, Av. 24 de Julho 49, 1200-109 Lisboa, Portugal", details: "Browse the food hall.", icon: Utensils, mapUrl: "" },
        { time: "4:00–5:00 PM", activity: "Try Triângulo da Ribeira", location: "Time Out Market Lisboa, Av. 24 de Julho 49, 1200-109 Lisboa, Portugal", details: "Food stall at the market.", icon: Utensils, mapUrl: "" },
        { time: "5:30 PM", activity: "QUEST · Cinnamon Buns", location: "Lisbon", details: "Cinnamon bun stop before the cruise.", icon: Utensils, mapUrl: "" },
      ],
      evening: [
        { time: "6:45 PM", activity: "Arrive for sunset boat tour", location: "Estação Fluvial Sul e Sueste, 1B, Av. Infante Dom Henrique, Lisboa, Portugal", details: "Be there by 6:45 PM for the 7:00 PM departure.", icon: MapPin, mapUrl: "" },
        { time: "7:00 PM", activity: "Sunset boat tour", location: "Estação Fluvial Sul e Sueste, 1B, Av. Infante Dom Henrique, Lisboa, Portugal", details: "$49.85 pp · all 6 travelers ($299.10 total). Inside the station, booth Nº3 — BLUE CRUISES to exchange your voucher. Sundeck access limited to the first 52 passengers.", icon: Anchor, mapUrl: "" },
      ],
    },
    notes: [
      "FlixBus Albufeira → Lisbon: be at Av. da Liberdade 136 by 10:30 AM for an 11:00 AM departure.",
      "Lisbon Airbnb: Rua Cavaleiros 67, Lisbon, Lisbon 1100.",
      "Boat tour: arrive by 6:45 PM · booth Nº3 BLUE CRUISES · sundeck first 52 passengers only.",
    ],
    overviewActivities: [
      "Pack up in Albufeira and take FlixBus back to Lisbon.",
      "Check into the Lisbon Airbnb, then Pink Street, Time Out Market, and QUEST cinnamon buns.",
      "Sunset boat tour from Estação Fluvial Sul e Sueste (BLUE CRUISES, booth Nº3).",
    ],
    gettingAround: "FlixBus Albufeira → Lisbon. Walking + Ubers in Lisbon.",
  },
  {
    day: 7,
    date: "May 23",
    dateFull: "SAT MAY 23",
    title: "Belém + LX Factory (Riverside Day)",
    description: "Belém sights, pastéis, optional MAAT and riverside spots — or split up and explore on your own.",
    color: "bg-purple-500",
    colorLight: "bg-purple-50",
    colorText: "text-purple-600",
    image: P7,
    activities: {
      morning: [
        { time: "7:30 AM", activity: "Wake up", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Start the riverside day.", icon: Home, mapUrl: "" },
        { time: "8:30 AM", activity: "Leave Airbnb", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Head toward Belém.", icon: Car, mapUrl: "" },
        { time: "9:00–10:00 AM", activity: "Breakfast", location: "Airbnb or nearby", details: "Eat at the Airbnb or grab something close by.", icon: Utensils, mapUrl: "" },
        { time: "9:30–10:30 AM", activity: "Extra breakfast (optional)", location: "Onze:Onze cafe, R. dos Jerónimos 14D, 1400-206 Lisboa, Portugal", details: "Only if you're still hungry.", icon: Utensils, mapUrl: "" },
        { time: "9:30–11:00 AM", activity: "Sightseeing", location: "Jerónimos Monastery, Praça do Império 1400-206 Lisboa, Portugal", details: "Also Jardim Vasco da Gama and Jardim Afonso de Albuquerque (Garden of Afonso de Albuquerque).", icon: Camera, mapUrl: "" },
        { time: "10:30 AM", activity: "Pastéis de Belém", location: "Pastéis de Belém, R. de Belém 84–92, 1300-085 Lisboa, Portugal", details: "Classic pastéis de nata stop.", icon: Utensils, mapUrl: "" },
      ],
      afternoon: [
        { time: "11:30 AM–3:00 PM", activity: "Belém & riverside", location: "Belém · MAAT · LX Factory area", details: "Options: MAAT, Belém district, LX Factory — or everyone splits up for the day.", icon: MapPin, mapUrl: "" },
      ],
    },
    notes: [
      "Optional extra breakfast: Onze:Onze (R. dos Jerónimos 14D) if hungry after Airbnb breakfast.",
      "Sightseeing: Jerónimos Monastery, Jardim Vasco da Gama, Jardim Afonso de Albuquerque.",
      "Also on the list: MAAT, Belém district, LX Factory — or split up and do your own thing.",
    ],
    overviewActivities: [
      "Breakfast at or near the Airbnb, then Belém sights and Pastéis de Belém.",
      "Optional Onze:Onze stop and time around Jerónimos and the gardens.",
      "Afternoon flexible: MAAT, Belém, LX Factory, or split up for the day.",
    ],
    gettingAround: "Uber/metro to Belém from the Airbnb. Walking around Belém and the riverside.",
  },
  {
    day: 8,
    date: "May 24",
    dateFull: "SUN MAY 24",
    title: "Day Trip to Sintra + Cabo da Roca",
    description: "Train to Sintra, Pena Palace and Moorish Castle, dinner in town, then sunset at Europe’s westernmost point.",
    color: "bg-cyan-500",
    colorLight: "bg-cyan-50",
    colorText: "text-cyan-600",
    image: P8,
    activities: {
      morning: [
        { time: "8:00 AM", activity: "Wake up", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Early start for Sintra.", icon: Home, mapUrl: "" },
        { time: "9:00 AM", activity: "Leave Airbnb", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Head out for breakfast and the train.", icon: Car, mapUrl: "" },
        { time: "9:00–10:00 AM", activity: "Breakfast", location: "Lisbon", details: "Eat before catching the train to Sintra.", icon: Utensils, mapUrl: "" },
        { time: "10:00–11:00 AM", activity: "Train to Sintra", location: "Lisbon → Sintra (train)", details: "Ride out to Sintra.", icon: Car, mapUrl: "" },
        { time: "11:00 AM–12:00 PM", activity: "Explore Sintra / lunch", location: "Sintra historic center", details: "Wander town and grab lunch.", icon: MapPin, mapUrl: "" },
        { time: "12:00 PM", activity: "Uber to Pena Palace grounds", location: "Parque da Pena, Estrada da Pena, 2710-609 Sintra, Portugal", details: "Ride up toward the palace.", icon: Car, mapUrl: "" },
        { time: "12:30–12:45 PM", activity: "Walk up to Pena Palace", location: "Palácio Nacional da Pena, Sintra", details: "Hike up from the grounds.", icon: MapPin, mapUrl: "" },
      ],
      afternoon: [
        { time: "1:30–2:30 PM", activity: "Pena Palace", location: "Palácio Nacional da Pena, Estrada da Pena, 2710-609 Sintra, Portugal", details: "Arrive 30 min–1 hr early for entry if needed.", icon: Camera, mapUrl: "" },
        { time: "2:30–3:30 PM", activity: "Moorish Castle", location: "Castelo dos Mouros, 2710-405 Sintra, Portugal", details: "Castelo dos Mouros.", icon: Camera, mapUrl: "" },
        { time: "3:30–4:30 PM", activity: "Walk back to town", location: "Sintra historic center", details: "Head down into Sintra.", icon: MapPin, mapUrl: "" },
        { time: "4:30–6:00 PM", activity: "Dinner", location: "Sintra", details: "Dinner in town.", icon: Utensils, mapUrl: "" },
        { time: "6:00–7:00 PM", activity: "Chill / walk around", location: "Sintra", details: "Optional extra time in town.", icon: Sun, mapUrl: "" },
      ],
      evening: [
        { time: "7:00–7:30 PM", activity: "Uber to Cabo da Roca", location: "Cabo da Roca, Estrada do Cabo da Roca s/n, 2705-001 Colares, Portugal", details: "Ride out for sunset.", icon: Car, mapUrl: "" },
        { time: "7:30–8:30 PM", activity: "Sunset at Cabo da Roca", location: "Cabo da Roca, Estrada do Cabo da Roca s/n, 2705-001 Colares, Portugal", details: "Westernmost point of mainland Europe.", icon: Sunset, mapUrl: "" },
        { time: "8:30–9:30 PM", activity: "Return to Lisbon", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "See notes for Uber vs bus + train options.", icon: Car, mapUrl: "" },
      ],
    },
    notes: [
      "Pena Palace: plan to arrive 30 min–1 hr before your entry window if tickets are timed.",
      "Getting home from Cabo da Roca: (1) Uber direct ~$100 CAD total, (2) Bus 1642 (~45 min) + Uber from Sintra station to Airbnb (~$50 Uber), or (3) Bus + train (~1h 28min, ~$8–14 pp · ~$84 total for 6 — Ubers are scarce at Cabo da Roca so plan ahead).",
    ],
    overviewActivities: [
      "Breakfast in Lisbon, then train to Sintra for town time and lunch.",
      "Pena Palace and Moorish Castle, dinner in Sintra, then Cabo da Roca for sunset.",
      "Return via Uber, bus + Uber, or bus + train — see notes for costs.",
    ],
    gettingAround: "Train Lisbon ↔ Sintra · Ubers in Sintra · Cabo da Roca return options in notes.",
  },
  {
    day: 9,
    date: "May 25",
    dateFull: "MON MAY 25",
    title: "Day Trip to Cascais",
    description: "Train from Cais do Sodré to Cascais — bakery breakfast, beach, gelato, lunch, lighthouse walk, and dinner in town.",
    color: "bg-indigo-500",
    colorLight: "bg-indigo-50",
    colorText: "text-indigo-600",
    image: P9,
    activities: {
      morning: [
        { time: "7:00 AM", activity: "Wake up", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Early start for Cascais.", icon: Home, mapUrl: "" },
        { time: "8:00 AM", activity: "Leave Airbnb", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "~11 min bus or ~26 min walk to the station.", icon: Car, mapUrl: "" },
        { time: "8:30 AM", activity: "Arrive at Cais do Sodré · buy train tickets", location: "Cais do Sodré, 1200-161 Lisboa, Portugal", details: "Buy tickets at the station (trains every ~20 min). Sit on the left side for coastal views.", icon: MapPin, mapUrl: "" },
        { time: "8:30–9:30 AM", activity: "Train to Cascais", location: "Cais do Sodré → Cascais", details: "~40 min ride to Cascais.", icon: Car, mapUrl: "" },
        { time: "9:30 AM", activity: "Arrive in Cascais", location: "Largo da Estação, 2750-340 Cascais, Portugal", details: "Cascais train station.", icon: MapPin, mapUrl: "" },
        { time: "10:00 AM", activity: "Breakfast at Padaria Portuguesa", location: "The Portuguese Bakery, R. Visc. Luz 17C, 2750-415 Cascais, Portugal", details: "Try pão com chouriço and pão de Deus; explore the area.", icon: Utensils, mapUrl: "" },
        { time: "11:00 AM", activity: "Praia da Rainha (beach)", location: "Praia da Rainha, Cascais", details: "Beach time in Cascais.", icon: Waves, mapUrl: "" },
        { time: "11:00 AM", activity: "Gelato", location: "Santini Gelados Artesanais – Cascais, Av. Valbom 28F, 2750-508 Cascais, Portugal", details: "Artisan gelato by the beach.", icon: Utensils, mapUrl: "" },
      ],
      afternoon: [
        { time: "1:00 PM", activity: "Lunch", location: "House of Wonders, R. da Misericórdia 53, 2750-642 Cascais, Portugal", details: "Lunch in Cascais.", icon: Utensils, mapUrl: "" },
        { time: "3:00 PM", activity: "Walk to Santa Maria & mirador", location: "Farol de Santa Maria, Praceta Farol, 2750-642 Cascais, Portugal", details: "Also stop at Miradouro Casa de Santa María, Av. Rei Humberto II de Itália 7, 2750-642 Cascais, Portugal.", icon: Camera, mapUrl: "" },
      ],
      evening: [
        { time: "6:00 PM", activity: "Dinner", location: "Cascais", details: "Pick one: Mozzafiatto · Gandhi Palace · Sr. Manuel · Marítimo (seafood).", icon: Utensils, mapUrl: "" },
      ],
    },
    notes: [
      "Train: Cais do Sodré → Cascais · buy tickets at the station · ~every 20 min · sit on the left side.",
      "From the Airbnb: ~11 min bus or ~26 min walk to Cais do Sodré.",
      "Trains run back to Lisbon regularly in the evening.",
    ],
    overviewActivities: [
      "Train from Cais do Sodré to Cascais (sit on the left).",
      "Breakfast at Padaria Portuguesa, beach at Praia da Rainha, and Santini gelato.",
      "Lunch at House of Wonders, lighthouse walk, then dinner in Cascais.",
    ],
    gettingAround: "Bus or walk to Cais do Sodré · CP train to Cascais · walking in town.",
  },
  {
    day: 10,
    date: "May 26",
    dateFull: "TUE MAY 26",
    title: "Lisbon: Alfama + Markets + Pack",
    description: "Breakfast, Alfama sightseeing + Feira da Ladra, snacks, then pack and a final dinner in Lisbon.",
    color: "bg-gray-500",
    colorLight: "bg-gray-50",
    colorText: "text-gray-600",
    image: P10,
    activities: {
      morning: [
        { time: "7:00–7:30 AM", activity: "Wake up", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Easy start to the day.", icon: Home, mapUrl: "" },
        { time: "8:00 AM", activity: "Leave Airbnb", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Head out for breakfast.", icon: Car, mapUrl: "" },
        { time: "8:30–10:00 AM", activity: "Breakfast", location: "Neighbourhood Cafe Lisbon, Largo da Graça 13, 1170-296 Lisboa, Portugal", details: "Breakfast in Graça.", icon: Utensils, mapUrl: "" },
        { time: "10:00–10:30 AM", activity: "Walk to Alfama", location: "Graça → Alfama", details: "Scenic walk into Alfama.", icon: MapPin, mapUrl: "" },
        { time: "10:30 AM–12:30 PM", activity: "Sightsee + flea market", location: "Campo de Santa Clara, 1100-471 Lisboa, Portugal", details: "National Pantheon, Feira da Ladra (local shopping), and Miradouro de Santa Luzia.", icon: Camera, mapUrl: "" },
      ],
      afternoon: [
        { time: "1:00 PM", activity: "Potato Project", location: "Rua da Conceição 8, 1100-226 Lisboa, Portugal", details: "Grab lunch/snack.", icon: Utensils, mapUrl: "" },
        { time: "1:30–2:00 PM", activity: "Arco da Rua Augusta", location: "Arco da Rua Augusta, Lisbon", details: "Quick stop for photos.", icon: Camera, mapUrl: "" },
        { time: "2:00–2:30 PM", activity: "Bifanas do Afonso", location: "R. da Assunção 80, 1100-044 Lisboa, Portugal", details: "Snack stop.", icon: Utensils, mapUrl: "" },
        { time: "3:00–3:30 PM", activity: "Manteigaria (pastéis)", location: "Manteigaria, Rua do Loreto 2, 1200-108 Lisboa, Portugal", details: "Pastel stop.", icon: Utensils, mapUrl: "" },
        { time: "4:00 PM", activity: "Back to Airbnb (optional)", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Head back if you want a reset before packing.", icon: Home, mapUrl: "" },
        { time: "4:00–6:00 PM", activity: "Pack", location: "Rua Cavaleiros 67, Lisbon, Lisbon 1100", details: "Get everything ready for departure day.", icon: Home, mapUrl: "" },
      ],
      evening: [
        { time: "7:00 PM", activity: "Dinner", location: "Café de São Bento - Baixa, R. do Jardim do Regedor 7, 1150-193 Lisboa, Portugal", details: "Final dinner in Lisbon.", icon: Utensils, mapUrl: "" },
      ],
    },
    notes: [
      "Airbnb address: Rua Cavaleiros 67, Lisbon, Lisbon 1100.",
      "Market stop: Feira da Ladra (local shopping) near the National Pantheon.",
      "Build in buffer for walking/transit between neighborhoods.",
    ],
    overviewActivities: [
      "Breakfast in Graça, then walk to Alfama for sights + flea market.",
      "Food/snack stops in the afternoon, then head back to pack.",
      "Dinner in Baixa to wrap up the day.",
    ],
    gettingAround: "Walking + Ubers/Metro as needed.",
  },
  {
    day: 11,
    date: "May 27",
    dateFull: "WED MAY 27",
    title: "Departure",
    description: "Time to head home with amazing memories.",
    color: "bg-slate-500",
    colorLight: "bg-slate-50",
    colorText: "text-slate-600",
    image: P1,
    activities: {
      evening: [
        { time: "11:30 PM", activity: "Flight back home", location: "Lisbon Airport", details: "Departure flight back to New York.", icon: Plane },
      ],
    },
    notes: [
      "Arrive at airport 3 hours before departure.",
      "Safe travels home!",
    ],
    overviewActivities: [
      "Travel to Lisbon Airport and depart for home.",
      "Wrap up the Portugal trip with final goodbyes.",
    ],
    gettingAround: "Airport transfer arranged.",
  },
];

type ViewMode = "overview" | "timeline" | "cards";

const Itinerary = () => {
  const [selectedDay, setSelectedDay] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>("timeline");
  const currentDay = days.find(d => d.day === selectedDay) || days[0];
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dayButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Auto-scroll to selected day
  useEffect(() => {
    const selectedIndex = selectedDay - 1;
    const button = dayButtonRefs.current[selectedIndex];
    const container = scrollContainerRef.current;
    
    if (button && container) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;
      const buttonLeft = buttonRect.left - containerRect.left + scrollLeft;
      const buttonWidth = buttonRect.width;
      const containerWidth = containerRect.width;
      
      // Center the button in the container
      const targetScroll = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
      
      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  }, [selectedDay]);

  return (
    <section id="itinerary" className="section-padding bg-background">
      <div className="container max-w-6xl mx-auto">
        <AnimatedSection className="flex flex-wrap items-end gap-3 md:gap-4 mb-5">
          <div>
            <span className="text-primary font-medium tracking-widest uppercase text-sm">
              Day by Day
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold mt-2 mb-0">
              Trip Itinerary
            </h2>
          </div>
          <div className="inline-flex rounded-full bg-muted p-1 text-[11px] md:text-sm shrink-0 ml-auto">
            <button
              type="button"
              onClick={() => setViewMode("overview")}
              className={`px-3 py-1.5 rounded-full transition-colors ${
                viewMode === "overview"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Overview
            </button>
            <button
              type="button"
              onClick={() => setViewMode("timeline")}
              className={`px-3 py-1.5 rounded-full transition-colors ${
                viewMode === "timeline"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Timeline
            </button>
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={`px-3 py-1.5 rounded-full transition-colors ${
                viewMode === "cards"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Cards
            </button>
          </div>
        </AnimatedSection>

        {/* Horizontal Day Navigation */}
        <div 
          ref={scrollContainerRef}
          className="mb-8 overflow-x-auto pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          <div className="flex gap-4 min-w-max px-2">
            {days.map((day, index) => (
              <motion.button
                key={day.day}
                ref={(el) => { dayButtonRefs.current[index] = el; }}
                onClick={() => setSelectedDay(day.day)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative flex flex-col w-32 md:w-40 h-32 md:h-40 rounded-xl transition-all duration-300 overflow-hidden ${
                  selectedDay === day.day
                    ? "shadow-lg scale-105"
                    : "hover:shadow-md"
                }`}
              >
                {/* Image Section */}
                <div
                  className="relative h-20 md:h-24 w-full flex-shrink-0"
                  style={{
                    backgroundImage: `url(${day.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {selectedDay === day.day && (
                    <motion.div
                      layoutId="selectedDay"
                      className="absolute inset-0 rounded-t-xl border-2 border-white z-20"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </div>
                
                {/* Content Section with White Background */}
                <div className="bg-white flex flex-col items-center justify-center rounded-b-xl flex-1 min-h-0">
                  <span className="text-xs font-bold mb-1 text-black">DAY {day.day}</span>
                  <span className="text-xs md:text-sm font-medium text-center px-2 text-black line-clamp-2">
                    {day.title}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Selected Day Banner */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedDay}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="relative h-48 md:h-64 rounded-2xl overflow-hidden mb-6"
            style={{
              backgroundImage: `url(${currentDay.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className={`absolute inset-0 opacity-30`} />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40" />
            <div className="relative h-full flex items-center justify-center">
              <div className="absolute top-4 left-4 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                <span className="text-white text-xs font-bold">DAY {currentDay.day}</span>
              </div>
              <div className="text-center text-white">
                <p className="text-sm md:text-base font-medium mb-2">{currentDay.dateFull}</p>
                <h3 className="text-2xl md:text-4xl font-serif font-bold">{currentDay.title}</h3>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Day Details Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedDay}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="glass-card rounded-2xl p-6 md:p-8"
          >
          {/* Description */}
          <div className={`${currentDay.colorLight} rounded-xl p-3 md:p-4 mb-6`}>
            <p className="text-sm md:text-base text-foreground leading-relaxed">
              {currentDay.description}
            </p>
          </div>

          {/* Activities by Time Period */}
          {viewMode === "timeline" ? (
            <div className="mb-6">
              <div className="relative pl-8 sm:pl-10 mt-3">
                {/* Vertical timeline line */}
                <div className={`absolute left-3 sm:left-4 top-0 bottom-0 w-1 rounded-full opacity-40 ${currentDay.color}`} />

                {/* Timeline sections: morning / afternoon / evening / all day */}
                {(
                  [
                    { key: "morning", label: "Morning", icon: "🌅" },
                    { key: "afternoon", label: "Afternoon", icon: "🌞" },
                    { key: "evening", label: "Evening", icon: "🌙" },
                    { key: "allDay", label: "All Day", icon: "☀️" },
                  ] as const
                ).map((section) => {
                  const items = (currentDay.activities as any)[section.key] as
                    | { time: string; activity: string; location: string; details: string; mapUrl?: string }[]
                    | undefined;

                  if (!items || items.length === 0) return null;

                  return (
                    <div key={section.key} className="relative mb-5 last:mb-0">
                      {/* Section icon on the line */}
                      <div
                        className={`absolute left-3 sm:left-4 top-0 h-8 w-8 rounded-full flex items-center justify-center -translate-x-1/2 shadow-lg border-4 border-background ${currentDay.color}`}
                      >
                        <span className="text-sm" aria-hidden="true">
                          {section.icon}
                        </span>
                      </div>

                      <div className="ml-6 sm:ml-8 pb-1 space-y-1.5">
                        {/* Section label pill */}
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span
                            className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${currentDay.colorLight} ${currentDay.colorText}`}
                          >
                            {section.label}
                          </span>
                        </div>

                        {/* All activities in this period */}
                        <div className="mt-1.5 space-y-3">
                          {items.map((item, idx) => (
                            <div key={idx} className="space-y-0.5">
                              <p className="text-xs sm:text-sm font-semibold text-muted-foreground">
                                {item.time}
                              </p>
                              <p className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                                {item.activity}
                              </p>
                              <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
                                <MapPin className="w-3 h-3" />
                                <a
                                  href={item.mapUrl || `https://www.google.com/maps?q=${encodeURIComponent(item.location)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline decoration-dotted underline-offset-2"
                                >
                                  {item.location}
                                </a>
                              </div>
                              {item.details && (
                                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                                  {item.details}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Divider between sections */}
                      <div className="mt-3 ml-6 sm:ml-8 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                    </div>
                  );
                })}
              </div>
            </div>
          ) : viewMode === "cards" ? (
            <div className="space-y-6 mb-6">
              {(
                [
                  { key: "morning", label: "Morning" },
                  { key: "afternoon", label: "Afternoon" },
                  { key: "evening", label: "Evening" },
                  { key: "allDay", label: "All Day" },
                ] as const
              ).map((section) => {
                const items = (currentDay.activities as any)[section.key] as
                  | { time: string; activity: string; location: string; details: string; icon?: any; mapUrl?: string }[]
                  | undefined;

                if (!items || items.length === 0) return null;

                return (
                  <div key={section.key}>
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${currentDay.colorLight} ${currentDay.colorText}`}
                      >
                        {section.label}
                      </span>
                    </div>
                    <div className="space-y-3 ml-1 md:ml-3">
                      {items.map((item, idx) => {
                        const IconComponent = (item.icon || Sun) as React.ComponentType<{ className?: string }>;
                        return (
                          <div
                            key={idx}
                            className="rounded-2xl border border-border/70 bg-background/80 px-3 py-3 text-xs sm:text-sm leading-relaxed flex gap-3"
                          >
                            <div
                              className={`flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full ${currentDay.color} text-white shrink-0`}
                            >
                              <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs sm:text-sm font-semibold text-muted-foreground">
                                {item.time}
                              </p>
                              <p className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                                {item.activity}
                              </p>
                              <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground mt-0.5">
                                <MapPin className="w-3 h-3" />
                                <a
                                  href={item.mapUrl || `https://www.google.com/maps?q=${encodeURIComponent(item.location)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline decoration-dotted underline-offset-2"
                                >
                                  {item.location}
                                </a>
                              </div>
                              {item.details && (
                                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-1">
                                  {item.details}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4 mb-6">
              {currentDay.overviewActivities && currentDay.overviewActivities.length > 0 ? (
                <>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Overview for this day
                  </p>
                  <ul className="ml-4 md:ml-6 list-disc space-y-1.5 text-xs sm:text-sm text-muted-foreground">
                    {currentDay.overviewActivities.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  {currentDay.activities.morning && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">
                        Morning
                      </p>
                      <ul className="ml-4 md:ml-6 list-disc space-y-1 text-xs sm:text-sm text-muted-foreground">
                        {currentDay.activities.morning.map((activity, idx) => (
                          <li key={idx}>{activity.activity}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {currentDay.activities.afternoon && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">
                        Afternoon
                      </p>
                      <ul className="ml-4 md:ml-6 list-disc space-y-1 text-xs sm:text-sm text-muted-foreground">
                        {currentDay.activities.afternoon.map((activity, idx) => (
                          <li key={idx}>{activity.activity}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {currentDay.activities.evening && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">
                        Evening
                      </p>
                      <ul className="ml-4 md:ml-6 list-disc space-y-1 text-xs sm:text-sm text-muted-foreground">
                        {currentDay.activities.evening.map((activity, idx) => (
                          <li key={idx}>{activity.activity}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {"allDay" in currentDay.activities && currentDay.activities.allDay && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">
                        All day
                      </p>
                      <ul className="ml-4 md:ml-6 list-disc space-y-1 text-xs sm:text-sm text-muted-foreground">
                        {(currentDay.activities as { allDay: { activity: string }[] }).allDay.map((activity, idx) => (
                          <li key={idx}>{activity.activity}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Notes Section */}
          {currentDay.notes && (
            <div className={`${currentDay.colorLight} rounded-xl p-4 mb-6`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`font-bold ${currentDay.colorText}`}>NOTES</span>
                <span className="text-xs text-muted-foreground">Important details for this day</span>
              </div>
              <ul className="space-y-2">
                {currentDay.notes.map((note, idx) => (
                  <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Getting Around Section */}
          {currentDay.gettingAround && (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 text-red-600">
                <Car className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">GETTING AROUND</p>
                <p className="text-sm text-muted-foreground">{currentDay.gettingAround}</p>
              </div>
            </div>
          )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Itinerary;
